/**
 * The 3D view's camera keys: held W/A/S/D move the camera forward, left,
 * back and right, and Q/E down and up, as in other 3D programs. The keys are
 * claimed only while the view shows, never while typing into a field; which
 * presses count as held is HeldKeyTracker's rule. The view's frame loop moves
 * the camera by the held keys once a frame.
 */

import type { View3DCore } from '@kaiizen/kai-floor-plan-editor-core';
import { isTypingInField } from '../isTypingInField';
import { HeldKeyTracker, type KeyMotion } from './viewTools';

/** How fast held keys move the camera, in screen px per second at its target. */
const KEY_MOTION_PX_PER_SECOND = 480;
/** The longest step one frame of held-key motion takes, so a stalled frame never lurches. */
const KEY_MOTION_MAX_FRAME_SECONDS = 0.05;

/** The camera moves the keys drive. */
type KeyCamera = Pick<View3DCore, 'pan' | 'lift'>;

export class View3DKeyInput {
  private canvas: HTMLCanvasElement;
  private camera: KeyCamera;
  /** The view shows, so the camera keys are claimed. */
  private showing = false;
  /** The camera keys held down, and when the last frame moved by them. */
  private heldKeys = new HeldKeyTracker();
  private lastKeyFrame: number | null = null;
  /** Tears down every listener the constructor attached. */
  private listeners = new AbortController();

  constructor(canvas: HTMLCanvasElement, camera: KeyCamera) {
    this.canvas = canvas;
    this.camera = camera;
    const { signal } = this.listeners;
    window.addEventListener('keydown', event => this.keyDown(event), { signal });
    window.addEventListener('keyup', event => this.heldKeys.keyUp(event), { signal });
    window.addEventListener('blur', () => this.heldKeys.releaseAll(), { signal });
  }

  show(): void {
    this.showing = true;
  }

  /** The view hid: no camera key is claimed, and none stays held. */
  hide(): void {
    this.showing = false;
    this.heldKeys.releaseAll();
    this.lastKeyFrame = null;
  }

  /** One frame of held-key motion, scaled by the time since the last one: W/S and A/D pan, Q/E lift. */
  moveByHeldKeys(time: number): void {
    const seconds = this.lastKeyFrame === null ? 0 : Math.min(KEY_MOTION_MAX_FRAME_SECONDS, (time - this.lastKeyFrame) / 1000);
    const { motion } = this.heldKeys;
    this.lastKeyFrame = motion ? time : null;
    if (motion && seconds) this.moveCamera(motion, KEY_MOTION_PX_PER_SECOND * seconds);
  }

  dispose(): void {
    this.listeners.abort();
  }

  private keyDown(event: KeyboardEvent): void {
    if (this.showing && !isTypingInField(event) && this.heldKeys.keyDown(event)) event.preventDefault();
  }

  /** A pan drags the plan, so the camera moving forward and right drags it down and left. */
  private moveCamera({ right, forward, up }: KeyMotion, pixels: number): void {
    const { clientWidth: width, clientHeight: height } = this.canvas;
    if (right || forward) this.camera.pan(-right * pixels, forward * pixels, width, height);
    if (up) this.camera.lift(up * pixels, height);
  }
}
